import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { achatsApi } from '@/api/achats';
import { toast } from '@/stores/toastStore';

/**
 * SIMG-ACHATS-LEADBETON-2026-002 — Hooks TanStack Query pour le portail acheteur.
 */

const K_DASHBOARD = ['achats', 'dashboard'] as const;
const K_BCAS      = ['achats', 'bca'] as const;
const K_FOURN     = ['achats', 'fournisseurs'] as const;
const K_VAL_BCA   = ['validation', 'bca'] as const;
const K_VAL_COUNT = ['validation', 'count'] as const;

export const useAchatsDashboard = () =>
  useQuery({ queryKey: K_DASHBOARD, queryFn: achatsApi.dashboard, staleTime: 30_000 });

export const useMesBcas = (statut?: string) =>
  useQuery({
    queryKey:  [...K_BCAS, statut ?? 'all'] as const,
    queryFn:   () => achatsApi.mesBcas(statut),
    staleTime: 30_000,
  });

export const useFournisseurs = () =>
  useQuery({ queryKey: K_FOURN, queryFn: achatsApi.fournisseurs, staleTime: 60_000 });

export const useCreerBca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: achatsApi.creerBca,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: K_DASHBOARD });
      qc.invalidateQueries({ queryKey: K_BCAS });
      toast.success('BCA créé', data.reference);
    },
  });
};

export const useModifierBca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof achatsApi.modifierBca>[1] }) =>
      achatsApi.modifierBca(id, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: K_DASHBOARD });
      qc.invalidateQueries({ queryKey: K_BCAS });
      qc.invalidateQueries({ queryKey: K_VAL_COUNT });
      toast.success('BCA modifié', data.reference);
    },
  });
};

export const useSoumettreBca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: achatsApi.soumettreBca,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: K_DASHBOARD });
      qc.invalidateQueries({ queryKey: K_BCAS });
      qc.invalidateQueries({ queryKey: K_VAL_COUNT });
      toast.success('Soumis au DAF', data.reference);
    },
  });
};

// ─── Côté DAF (utilisé par l'écran /validation section Achats)

export const useBcaEnAttente = () =>
  useQuery({ queryKey: K_VAL_BCA, queryFn: achatsApi.bcaEnAttente, staleTime: 15_000 });

export const useValiderBca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: achatsApi.validerBca,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: K_VAL_BCA });
      qc.invalidateQueries({ queryKey: K_VAL_COUNT });
      qc.invalidateQueries({ queryKey: K_BCAS });
      qc.invalidateQueries({ queryKey: K_DASHBOARD });
      toast.success('BCA validé', data.reference);
    },
  });
};

export const useRenvoyerBca = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motif }: { id: string; motif: string }) => achatsApi.renvoyerBca(id, motif),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: K_VAL_BCA });
      qc.invalidateQueries({ queryKey: K_VAL_COUNT });
      qc.invalidateQueries({ queryKey: K_BCAS });
      qc.invalidateQueries({ queryKey: K_DASHBOARD });
      toast.success('Renvoyé pour correction', data.reference);
    },
  });
};
